import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-general-setting/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import AppCardHeader from 'src/components/card/AppCardHeader'
import AppTable from 'src/components/AppTable'

// helpers
import Axios from '../../helpers/axios'
import { columnLetterNumberList, columnCompanyBankAccountList } from '../../components/table-column/TableColumnList'
import { formUrlQuery } from 'src/helpers/format-queries'
import useToastMessage from 'src/helpers/toast-message'

const AddGeneralSetting = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();
  
  const [companyBankAccountDetail, setCompanyBankAccountDetail] = useState(null)
  
  const isLoading = useSelector((state) => state.isLoading)
  const [letterNumberListCount, setLetterNumberListCount] = useState(0)
  const [letterNumberListPerPage, setLetterNumberListPerPage] = useState(10);
  const [activeLetterNumberListPage, setActiveLetterNumberListPage] = useState(1);
  const [searchLetterNumberListFilter, setSearchLetterNumberListFilter] = useState("");
  const [columnLetterNumberListSorter, setColumnLetterNumberListSorter] = useState({ column: "createdAt", state: "DESC" });
  const [letterNumberList, setLetterNumberList] = useState([])

  const [companyBankAccountListCount, setCompanyBankAccountListCount] = useState(0)
  const [companyBankAccountListPerPage, setCompanyBankAccountListPerPage] = useState(10);
  const [activeCompanyBankAccountListPage, setActiveCompanyBankAccountListPage] = useState(1);
  const [searchCompanyBankAccountListFilter, setSearchCompanyBankAccountListFilter] = useState("");
  const [columnCompanyBankAccountListSorter, setColumnCompanyBankAccountListSorter] = useState({ column: "createdAt", state: "DESC" });
  const [companyBankAccountList, setCompanyBankAccountList] = useState([])

  const [showModalConfirm, setShowModalConfirm] = useState({ show: false, type: '', text: '' })
  const [showConfirmView, setShowConfirmView] = useState({ show: false, type: '', item: {} })
  const [showConfirmDelete, setShowConfirmDelete] = useState({ show: false, item: {} })

  const [showConfirmAddLetterNumber, setShowConfirmAddLetterNumber] = useState({ show: false })
  const [showConfirmAddCompanyBankAccount, setShowConfirmAddCompanyBankAccount] = useState({ show: false, item: {} })

  const [headerTitle, setHeaderTitle] = useState('')

  const [letterNumberValue, setLetterNumberValue] = useState(null)
  const [isErrLetterNumber, setIsErrLetterNumber] = useState(false)

  const formik = useFormik({
    initialValues: {
      max_forever_year: '',
      company_code: '',
      platform_name: '',
      platform_name_supported_team: '',
      signature_name: '',
    },
    validationSchema: Yup.object().shape({
      max_forever_year: Yup.string().required('Please enter max forever year'),
      company_code: Yup.string().required('Please enter company code'),
      platform_name: Yup.string().required('Please enter platform name'),
      platform_name_supported_team: Yup.string().required('Please enter name of supported team'),
      signature_name: Yup.string().required('Please enter signature name'),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  const formikCompanyBankAccount = useFormik({
    initialValues: {
      account_name: companyBankAccountDetail?.account_name || '',
      account_number: companyBankAccountDetail?.account_number || '',
      account_swift_code: companyBankAccountDetail?.account_swift_code || '',
      bank_name: companyBankAccountDetail?.bank_name || '',
      bank_address: companyBankAccountDetail?.bank_address || '',
      is_default_bank: companyBankAccountDetail?.is_default_bank === 'Yes' || companyBankAccountDetail?.is_default_bank === true ? true : false
    },
    validationSchema: Yup.object().shape({
      account_name: Yup.string().required('Please enter account name'),
      account_number: Yup.string().required('Please enter account number'),
      account_swift_code: Yup.string().required('Please enter account swift code'),
      bank_name: Yup.string().required('Please enter bank name'),
      bank_address: Yup.string().required('Please enter bank address')
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSaveCompanyBankAccount(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    let queries = handleQueriesLetterNumber()
    fetchAllLetterNumber(queries)
  }, [
    searchLetterNumberListFilter,
    letterNumberListPerPage,
    activeLetterNumberListPage,
    columnLetterNumberListSorter
  ])

  const handleQueriesLetterNumber = () => {
    let queries = {
      search: searchLetterNumberListFilter,
      limit: letterNumberListPerPage,
      offset: letterNumberListPerPage * activeLetterNumberListPage - letterNumberListPerPage,
      sort:
        (columnLetterNumberListSorter &&
          `${columnLetterNumberListSorter.column}%${columnLetterNumberListSorter.state}`) ||
        ""
    };
    queries = formUrlQuery(queries);

    return queries
  }

  useEffect(() => {
    let queries = handleQueriesCompanyBankAccount()
    fetchAllCompanyBankAccount(queries)
  }, [
    searchCompanyBankAccountListFilter,
    companyBankAccountListPerPage,
    activeCompanyBankAccountListPage,
    columnCompanyBankAccountListSorter
  ])

  const handleQueriesCompanyBankAccount = () => {
    let queries = {
      search: searchCompanyBankAccountListFilter,
      limit: companyBankAccountListPerPage,
      offset: companyBankAccountListPerPage * activeCompanyBankAccountListPage - companyBankAccountListPerPage,
      sort:
        (columnCompanyBankAccountListSorter &&
          `${columnCompanyBankAccountListSorter.column}%${columnCompanyBankAccountListSorter.state}`) ||
        "",
    };
    queries = formUrlQuery(queries);

    return queries
  }

  const fetchAllLetterNumber = (queries) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    Axios({
      method: "get",
      url: `/api/tracker/v1/generalsetting/letternumber/list?${queries}`,
    })
      .then(({ data }) => {
        const tempLetterNumberList = data?.value
        setLetterNumberList(tempLetterNumberList)
        if (activeLetterNumberListPage === 1) {
          setLetterNumberListCount(data?.count)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllCompanyBankAccount = (queries) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    Axios({
      method: "get",
      url: `/api/tracker/v1/generalsetting/companybankaccount/list?${queries}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          let tempCompanyBankAccountList = data?.value
          tempCompanyBankAccountList = tempCompanyBankAccountList?.map((item) => ({
            ...item,
            is_default_bank: item?.is_default_bank ? 'Yes' : 'No'
          })) || []
          setCompanyBankAccountList(tempCompanyBankAccountList)
          if (activeCompanyBankAccountListPage === 1) {
            setCompanyBankAccountListCount(data?.count)
          }
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSearchItemsLetterNumber = (search) => {
    setActiveLetterNumberListPage(1)
    setSearchLetterNumberListFilter(search);
  }

  const handleSearchItemsCompanyBankAccount = (search) => {
    setActiveCompanyBankAccountListPage(1)
    setSearchCompanyBankAccountListFilter(search);
  }

  const handleInputValue = (values) => {
    let data = {
      max_forever_year: values?.max_forever_year,
      company_code: values?.company_code,
      platform_name: values?.platform_name,
      platform_name_supported_team: values?.platform_name_supported_team,
      signature_name: values?.signature_name,
    }

    return data
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handlePost = (payload) => {
    let url = `/api/tracker/v1/generalsetting/create`

    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    
    Axios({
      method: "post",
      url: url,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          let dataSettings = data?.value
          localStorage.setItem("general_setting", JSON.stringify(dataSettings))
          showSuccess('General setting has been created successfully')
          navigate('/generalsetting')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleInputValueCompanyBankAccount = (values) => {
    let data = {
      account_name: values?.account_name,
      account_number: values?.account_number,
      account_swift_code: values?.account_swift_code,
      bank_name: values?.bank_name,
      bank_address: values?.bank_address,
      is_default_bank: values?.is_default_bank || false
    }

    return data
  }

  const handleSaveCompanyBankAccount = (values) => {
    const inputPayload = handleInputValueCompanyBankAccount(values)
    handlePostCompanyBankAccount(inputPayload)
  }

  const handleSubmit = () => {
    if (showModalConfirm.type == 'generalsetting') {
      formik.handleSubmit()
    }

    if (showModalConfirm.type == 'letternumber') {
      if (letterNumberValue?.value) {
        handleGenerateLetterNumber()
        setShowConfirmAddLetterNumber({ show: false })
        setLetterNumberValue(null)
      } else {
        setIsErrLetterNumber(true)
      }
    }

    if (showModalConfirm.type == 'companybankaccount') {
      formikCompanyBankAccount.handleSubmit()
    }

    setShowModalConfirm({ show: false, type: '', text: '' })
  }

  const handleGenerateLetterNumber = () => {
    const data = {
      letter_type: letterNumberValue?.value
    }

    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    
    Axios({
      method: "post",
      url: '/api/tracker/v1/generalsetting/create/letternumber',
      data
    })
      .then(({ data }) => {
        if (data?.success) {
          setShowConfirmAddLetterNumber({ show: false })
          fetchAllLetterNumber(handleQueriesLetterNumber())
          showSuccess('Letter number has been generated successfully')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handlePostCompanyBankAccount = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    let url = '/api/tracker/v1/generalsetting/create/companybankaccount'
    let actionName = 'created'

    if (showConfirmAddCompanyBankAccount?.item?.id) {
      url = `/api/tracker/v1/generalsetting/update/companybankaccount/${showConfirmAddCompanyBankAccount?.item?.id}`
      actionName = 'updated'
    }
    
    Axios({
      method: "post",
      url,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          setShowConfirmAddCompanyBankAccount({ show: false })
          fetchAllCompanyBankAccount(handleQueriesCompanyBankAccount())
          formikCompanyBankAccount.resetForm()
          setCompanyBankAccountDetail(null)
          showSuccess(`Company bank account has been ${actionName} successfully`)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleModalGenerateLetterNumber = () => {
    setShowConfirmAddLetterNumber({ show: true })
    setHeaderTitle("Generate Letter Number")
  }

  const handleModalCreateCompanyBankAccount = () => {
    setShowConfirmAddCompanyBankAccount({ show: true })
    setHeaderTitle("Create Company Bank Account")
  }

  useEffect(() => {
    if (!showConfirmAddLetterNumber.show) {
      setLetterNumberValue(null)
      setIsErrLetterNumber(false)
    }
  }, [showConfirmAddLetterNumber])

  useEffect(() => {
    if (!showConfirmAddCompanyBankAccount.show) {
      setCompanyBankAccountDetail(null)
      formikCompanyBankAccount.resetForm()
    }
  }, [showConfirmAddCompanyBankAccount])

  const handleSelectLetterNumber = (data) => {
    if (data && data.value) {
      setLetterNumberValue(data)
    }
  }

  const handleShowModalConfirm = (type, action = '') => {
    let text = ''
    if (type == 'generalsetting') {
      text = 'Are you sure want to create general setting?'
    }

    if (type === 'letternumber') {
      text = 'Are you sure want to generate new letter number?'
    }

    if (type === 'companybankaccount') {
      if (action === 'edit') {
        text = 'Are you sure want to edit this company bank account?'
      } else {
        text = 'Are you sure want to create new company bank account?'
      }
    }

    setShowModalConfirm({ show: !showModalConfirm.show, type, text })
  }

  const copyToClipboard = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      showSuccess('Letter number has been copied to your clipboard')
    } catch (err) {
      showError('Failed to copy!')
    }
  };

  const handleOnRowLetterNumberClick = (item, columnName) => {
    if (columnName !== 'action') {
      setShowConfirmView({ show: true, item, type: 'letternumber' })
    }
  }

  const handleOnRowCompanyBankAccountClick = (item, columnName) => {
    if (columnName !== 'action') {
      setShowConfirmView({ show: true, item, type: 'companybankaccount' })
    }
  }

  const handleShowModalEditCompanyBankAccount = (data) => {
    setCompanyBankAccountDetail(data.item)
    setShowConfirmAddCompanyBankAccount({ show: true, item: data.item })
    setHeaderTitle("Edit Company Bank Account")
  }

  const handleDeleteConfirmation = () => {
    if (showConfirmDelete?.type === 'company-bank-account') {
      Axios({
        method: "delete",
        url: `/api/tracker/v1/generalsetting/delete/companybankaccount/${showConfirmDelete?.item?.id}`,
      })
        .then(({ data }) => {
          if (data?.success) {
            setShowConfirmDelete({ show: false, item: {} })
            fetchAllCompanyBankAccount(handleQueriesCompanyBankAccount())
            showSuccess('Company bank account has been deleted successfully')
          }
        })
        .catch((err) => {
          console.error(err);
          const errMsg = err?.response?.data?.message
          showError(errMsg)
        });
    }
  }

  return (
    <>
      {
        showConfirmAddLetterNumber.show && (
          <AppModalConfirmation 
            confirmationType='add-letter-number'
            itemDetails={showConfirmAddLetterNumber.item}
            showModal={showConfirmAddLetterNumber}
            setShowModal={setShowConfirmAddLetterNumber}
            headerTitle={headerTitle}
            values={letterNumberValue}
            errors={isErrLetterNumber}
            handleSelectInput={handleSelectLetterNumber}
            handleSubmit={() => handleShowModalConfirm('letternumber')}
          />
        )
      }
      {
        showConfirmAddCompanyBankAccount.show && (
          <AppModalConfirmation
            confirmationType='add-company-bank-account'
            itemDetails={showConfirmAddCompanyBankAccount.item}
            showModal={showConfirmAddCompanyBankAccount}
            setShowModal={setShowConfirmAddCompanyBankAccount}
            headerTitle={headerTitle}
            values={formikCompanyBankAccount.values}
            handleChange={formikCompanyBankAccount.handleChange}
            errors={formikCompanyBankAccount.errors}
            handleSubmit={() => {
              if (showConfirmAddCompanyBankAccount?.item?.id) {
                handleShowModalConfirm('companybankaccount', 'edit')
              } else {
                handleShowModalConfirm('companybankaccount', 'create')
              }
            }}
          />
        )
      }
      {
        showConfirmView.show && (
          <AppModalConfirmation 
            confirmationType={showConfirmView.type === 'letternumber' ? 'detail-letter-number' : 'detail-company-bank-account'}
            itemName={showConfirmView.type === 'letternumber' ? 'detail-letter-number' : 'detail-company-bank-account'}
            itemDetails={showConfirmView.item}
            itemType={showConfirmView.type}
            showModal={showConfirmView}
            setShowModal={setShowConfirmView}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText={showModalConfirm.text}
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      {
        showConfirmDelete?.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={showConfirmDelete.type === 'company-bank-account' ? `Company Bank Account ${showConfirmDelete?.item?.account_name}` : ''}
            itemDetails={showConfirmDelete.item}
            itemType={showConfirmDelete.type}
            itemIndex={showConfirmDelete?.index}
            showModal={showConfirmDelete}
            setShowModal={setShowConfirmDelete}
            handleConfirmation={handleDeleteConfirmation}
          />
        )
      }
      <AppCard
        className='mt-4 mb-4'
        headerTitle={
          <AppCardHeader
            headerTitle={<h4>Add General Setting</h4>}
          />
        }
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
          />
        }
        lowerBodyContent={
          <>
            <AppCard 
              className='mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<h4>Letter Number</h4>}
                  headerButton="Generate new"
                  handleCardHeaderButton={handleModalGenerateLetterNumber}
                />
              }
              bodyContent={
                <AppTable
                  letterNumberListPage
                  tableFilter
                  tableColumns={columnLetterNumberList}
                  tableContent={letterNumberList}
                  contentPerPage={letterNumberListPerPage}
                  activeContentPage={activeLetterNumberListPage}
                  contentCount={letterNumberListCount}
                  searchContentFilter={searchLetterNumberListFilter}
                  isShowModalEdit={false}
                  isShowDropdownMenu={false}
                  isShowButtonCopy={true}
                  handleCopyToClipboard={copyToClipboard}
                  setSearchContentFilter={handleSearchItemsLetterNumber}
                  setActiveContentPage={setActiveLetterNumberListPage}
                  setContentPerPage={setLetterNumberListPerPage}
                  setColumnContentSorter={setColumnLetterNumberListSorter}
                  handleOnRowClick={handleOnRowLetterNumberClick}
                  isClickable={true}
                />
              }
            />
            <AppCard 
              className='mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<h4>Company Bank Account</h4>}
                  headerButton="Create new"
                  handleCardHeaderButton={handleModalCreateCompanyBankAccount}
                />
              }
              bodyContent={
                <AppTable
                  companyBankAccountListPage
                  tableFilter
                  tableColumns={columnCompanyBankAccountList}
                  tableContent={companyBankAccountList}
                  contentPerPage={companyBankAccountListPerPage}
                  activeContentPage={activeCompanyBankAccountListPage}
                  contentCount={companyBankAccountListCount}
                  searchContentFilter={searchCompanyBankAccountListFilter}
                  isShowModalEdit={true}
                  setShowConfirmEdit={handleShowModalEditCompanyBankAccount}
                  setShowConfirmDelete={setShowConfirmDelete}
                  setSearchContentFilter={handleSearchItemsCompanyBankAccount}
                  setActiveContentPage={setActiveCompanyBankAccountListPage}
                  setContentPerPage={setCompanyBankAccountListPerPage}
                  setColumnContentSorter={setColumnCompanyBankAccountListSorter}
                  handleOnRowClick={handleOnRowCompanyBankAccountClick}
                  isClickable={true}
                  isShowButtonDelete={companyBankAccountList?.length == 1 ? false : true}
                />
              }
            />
          </>
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate(`/generalsetting`)}
            onSave={() => handleShowModalConfirm('generalsetting')}
          />
        }
      />
    </>
  )
}

export default AddGeneralSetting